/**
 * Platzgrenzen der TwoPager-Vorlage im Generator (`generators/pptx_gen.py`).
 *
 * Sie liegen hier zentral, weil die Auswahl-UI und die Zusammenfassung dieselbe Zahl
 * brauchen: Ueberschreitet die Auswahl das Budget, schneidet der Generator still ab —
 * das FE begrenzt deshalb vorher.
 */

/**
 * Zeilen der Skill-Matrix auf Seite 1 (`SKILL_SLOTS`), verteilt auf zwei Spalten.
 * Kategorie-Ueberschrift und Fähigkeit belegen je eine Zeile: 1 Kategorie + 17
 * Fähigkeiten, 2 + 16, 3 + 15 und so weiter.
 */
export const SKILL_MATRIX_ROWS = 18;

/**
 * Branchen im Two-Pager. Der Generator setzt sie in der Seitenleiste als *eine* Zeile
 * hintereinander (`VALUE_SEPARATOR.join`), die Spalte ist 4,6 cm breit — mehr als drei
 * laufen dort ueber.
 */
export const MAX_TWO_PAGER_INDUSTRIES = 3;

/** Minimalform einer Fähigkeit, wie die Zeilenrechnung sie braucht. */
interface CountableSkill {
  id?: string;
  kategorie?: string;
}

/**
 * Distinkte Kategorien der Auswahl. Gezaehlt wird wie gruppiert wird: getrimmte Kategorie,
 * und die Gruppe ohne Kategorie zaehlt mit — sie bekommt in Tabelle und Dokument genauso
 * eine Ueberschriftszeile.
 */
export function selectedSkillCategories(
  skills: readonly CountableSkill[] | null | undefined,
  selectedIds: readonly string[],
): Set<string> {
  const selected = new Set(selectedIds);
  const categories = new Set<string>();
  for (const skill of skills ?? []) {
    if (skill.id !== undefined && selected.has(skill.id)) {
      categories.add(skill.kategorie?.trim() ?? '');
    }
  }
  return categories;
}

/** Kategorie einer Fähigkeit, so wie gruppiert und gezaehlt wird. */
export function skillCategoryKey(skill: CountableSkill): string {
  return skill.kategorie?.trim() ?? '';
}

export function skillCategoryCount(
  skills: readonly CountableSkill[] | null | undefined,
  selectedIds: readonly string[],
): number {
  return selectedSkillCategories(skills, selectedIds).size;
}

/**
 * Zeilen, die eine Auswahl in der Skill-Matrix belegt: je Fähigkeit eine, je Kategorie eine.
 *
 * Die Rechnung steht hier und nicht in den Komponenten, weil Checkliste (Parent) und Sperre
 * (Fähigkeiten-Kachel) dieselbe Zahl brauchen. Als zwei Ausdruecke ist sie in diesem Ticket
 * bereits zweimal auseinandergelaufen — einmal bei der leeren Kategorie, einmal beim
 * ungetrimmten Vergleich.
 */
export function usedSkillRows(
  skills: readonly CountableSkill[] | null | undefined,
  selectedIds: readonly string[],
): number {
  return selectedIds.length + skillCategoryCount(skills, selectedIds);
}
